import {
  DataChangeEvent,
  determineSort,
  ESortOptions,
  OnDataLoadProps,
  OnSelectedRows,
  OnSortProps,
  RowData,
  SortOptions
} from '@turntable/core'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import { RecordStatusBanner } from 'src/applications/Embed/components/RecordStatusBanner'
import * as Layout from 'src/applications/Embed/elements/LayoutElements'
import { EmbedsErrorBanner } from 'src/applications/Embed/fragments/EmbedsErrorBanner'
import { FooterButton } from 'src/applications/Embed/fragments/Footer'
import { Header } from 'src/applications/Embed/fragments/Header'
import { EmbedLayout } from 'src/applications/Embed/fragments/Layout'
import { Loader } from 'src/applications/Embed/fragments/Loader'
import { IScenePayload, SCENE } from 'src/applications/Embed/types/IScene'
import { DataTable } from 'src/applications/Oversight/components/DataTable'
import { useConfirmModal } from 'src/applications/Oversight/hooks/useConfirmModal'
import { useFetchWorkbookRows } from 'src/applications/Oversight/hooks/useFetchWorkbookRows'
import { TableValidationContext } from 'src/contexts/TableValidationContext'
import { useFilterWithCounts } from 'src/resources/hooks/useFilterWithCounts'
import { useTableFilters } from 'src/resources/hooks/useTableFilters'
import { getRoundedNumber } from 'src/resources/utils/getRoundedNumber'
import { defaultProcessingStages } from 'src/resources/utils/loadingStages'
import { useSmartMutation } from 'src/smart/hooks/useSmartMutation'
import { useSmartQuery } from 'src/smart/hooks/useSmartQuery'
import { SM_EVALUATE_BATCH } from 'src/smart/mutations/SM_EVALUATE_BATCH'
import { SM_QUEUE_UPDATE_RECORD_STATUS } from 'src/smart/mutations/SM_QUEUE_UPDATE_RECORD_STATUS'
import { SM_SUBMIT_BATCH } from 'src/smart/mutations/SM_SUBMIT_BATCH'
import { SM_UPDATE_WORKBOOK_ROWS } from 'src/smart/mutations/SM_UPDATE_WORKBOOK_ROWS'
import { queueUpdateRecordStatusVariables } from 'src/smart/mutations/types/queueUpdateRecordStatus'
import { SQ_GET_WORKBOOK_SHEETS } from 'src/smart/queries/SQ_GET_WORKBOOK_SHEETS'
import { useWorkbookUpdatedSubscription } from 'src/smart/queries/SQ_WORKBOOK_UPDATED_SUBSCRIPTION'
import { EValidationState } from 'src/types/enums/EValidationState'
import { EWorkbookStatus } from 'src/types/enums/EWorkbookStatus'
import { Schema } from 'src/utils/data/Schema'
import { DALTransport } from 'src/utils/data/transports/DALTransport'
import { Transport } from 'src/utils/data/transports/Transport'

import {
  CancelButton,
  ControlBar,
  DismissButton,
  EllipsisLoader,
  EmptyState,
  FlexEndFooter,
  GoBackButton
} from 'src/applications/Embed/elements/SceneElements'

import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'

const PAGE_SIZE = 500
const LOADING_ERROR_TIMEOUT = 15000 /* 15 seconds */

export const ReviewScene = ({
  batch,
  onGoBack,
  onComplete,
  onCancelWorkbook,
  workbookStatus
}: IScenePayload) => {
  // used as control to only render data table if all data is loaded from the back end
  const { validatingTable, setValidatingTable } = useContext(TableValidationContext)
  const workspaceSheets = useSmartQuery(SQ_GET_WORKBOOK_SHEETS, {
    variables: { workspaceId: batch.workspaceId },
    fetchPolicy: 'network-only'
  })
  const { t } = useTranslation()

  const sheet = useMemo(
    () => workspaceSheets.result?.find((sheet) => sheet.schemaId === parseInt(batch.schemaId, 10)),
    [workspaceSheets, batch.schemaId]
  )

  const percentCompleteRounded = getRoundedNumber(sheet?.percentComplete)
  const workbookLoading =
    workbookStatus === EWorkbookStatus.IN_PROGRESS || workbookStatus === EWorkbookStatus.QUEUED
  const loading = sheet?.percentComplete < 99 || workbookLoading

  useEffect(() => {
    if (loading) {
      workspaceSheets.state.startPolling(1000)
    } else {
      workspaceSheets.state.stopPolling()
    }
  }, [loading])

  useEffect(() => {
    if (!workbookLoading) {
      setValidatingTable(false)
    }
  }, [workbookLoading])

  const content = useMemo(() => {
    if (validatingTable || !loading) {
      return (
        <ReviewSceneContent
          batch={batch}
          onGoBack={onGoBack}
          onComplete={onComplete}
          sheet={sheet}
          validating={validatingTable || loading}
        />
      )
    }

    return (
      <Loader
        top={104}
        displayLoadingCircle={percentCompleteRounded > 1}
        percentage={percentCompleteRounded}
        loadingStages={defaultProcessingStages}
        message={t('scenes.5_ReviewScene.loadingData')}
      >
        <CancelButton onClick={onCancelWorkbook} />
      </Loader>
    )
  }, [validatingTable, loading, percentCompleteRounded])

  return <EmbedLayout>{content}</EmbedLayout>
}

export const ReviewSceneContent = ({
  batch,
  onGoBack,
  onComplete,
  sheet,
  validating
}: IScenePayload) => {
  /*
    These properties persist loaded data in the component state in order get around the fact
    that useFetchWorkbookRows sets all values to empty while loading, regardless of whether
    data already exists.
  */
  const [newRows, setNewRows] = useState(undefined)
  const [newCounts, setNewCounts] = useState(undefined)
  /* ** */

  /* Other state behavior */
  const [loadingTimeoutError, setLoadingTimeoutError] = useState(false)
  const [saving, setSaving] = useState(false)
  const { setValidatingTable } = useContext(TableValidationContext)

  const [selectedRows, setSelectedRows] = useState<RowData[]>([])
  const [refreshingDataset, setRefreshingDataset] = useState(false)
  const [{ filter, errors: errorFilter }, setTableFilters] = useTableFilters()
  const [sortOverride, setSortOverride] = useState<{
    sortOrder: SortOptions
    sortColumn: number | undefined
  }>({ sortOrder: ESortOptions.noOrder, sortColumn: undefined })
  /* ** */

  /* Constants and mutations */
  const { t } = useTranslation()
  const { addToast } = useToasts()
  const theme: any = useTheme()

  const queueUpdateRecordStatus = useSmartMutation(SM_QUEUE_UPDATE_RECORD_STATUS)
  const submitBatch = useSmartMutation(SM_SUBMIT_BATCH)
  const evaluateBatch = useSmartMutation(SM_EVALUATE_BATCH)
  const updateValidationRows = useSmartMutation(SM_UPDATE_WORKBOOK_ROWS)

  const schemaObj = sheet?.template?.jsonSchema?.schema ?? batch?.schema
  const schemaProperties = schemaObj?.properties
  /* ** */

  /* Effects */
  useEffect(() => {
    setSelectedRows([])
  }, [filter, errorFilter])

  const {
    accepted,
    totalRows,
    filtered,
    counts,
    errors,
    rows,
    labels,
    alert: workbookLoading,
    fetchMoreRows,
    refetchRows,
    review,
    loading
  } = useFetchWorkbookRows(
    batch.workbookId,
    filter,
    'review',
    schemaObj,
    parseInt(batch.schemaId, 10),
    PAGE_SIZE,
    errorFilter,
    undefined,
    undefined,
    sortOverride.sortColumn,
    sortOverride.sortOrder
  )

  useEffect(() => {
    /* See comment at the top of this component for why we persist this information in the state. */
    if (!loading && !workbookLoading && !refreshingDataset) {
      if (!newRows || (!newRows.length && rows.length)) {
        if (rows) setNewRows(rows)
      }
      if (counts) setNewCounts(counts)
    }
  }, [rows, counts, loading, workbookLoading, refreshingDataset])

  const isLoadingData = loading || workbookLoading || refreshingDataset || validating // Whether data is currently being fetched from the API
  const isLoadingFullPage = loading || workbookLoading // Whether a full new set of data is being loaded, eg when sorting or switching to a new tab
  const isEmpty = !newRows || !newRows?.length // Whether row data exists
  const hasNoValidRows = newCounts?.valid === 0

  //hacky but we need to pass in the total count as the key for the table so the table updates correctly when rows are deleted
  const totalCount = useMemo(() => {
    if (counts) {
      return Object.values(counts)
        .map((value) => Number(value))
        .reduce((a, b) => a + b, 0)
    }
    return totalRows ?? 0
  }, [counts, totalRows])

  useEffect(() => {
    /*
      Putting this timeout in a useEffect ensures that we run it only once instead
      of on every render.
    */
    const loadingErrorTimeout = setTimeout(() => {
      if (isEmpty) {
        setLoadingTimeoutError(true)
      }
    }, LOADING_ERROR_TIMEOUT)

    /* Clears the timeout in case the user navigates away before it completes. */
    return () => clearTimeout(loadingErrorTimeout)
  }, [])

  useWorkbookUpdatedSubscription(batch.workbookId, {
    onSubscriptionData: async () => {
      setRefreshingDataset(true)
      await refetchRows()
      setRefreshingDataset(false)
    }
  })
  /* ** **/

  /* Action handlers */
  const batchHandlerMutation = (type: 'submit' | 'evaluate') => {
    switch (type) {
      case 'evaluate':
        return evaluateBatch.run({
          batchId: batch.batchId
        })
      case 'submit':
        return submitBatch.run({
          batchId: batch.batchId,
          dismissRemainingRecords: batch.status === 'evaluate'
        })
    }
  }

  const onBatchAction = async (type: 'submit' | 'evaluate') => {
    try {
      setSaving(true)
      const updatedBatch = await batchHandlerMutation(type)
      onComplete(updatedBatch)
    } catch ({ message }) {
      addToast(message, {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setSaving(false)
    }
  }

  const handleComplete = useCallback(async () => {
    if (newCounts.invalid > 0 && batch.synced) {
      DiscardInvalidRecordsModal.open()
      return
    }
    if (batch.synced) {
      return onBatchAction('evaluate')
    }
    onBatchAction('submit')
  }, [batch, newCounts])

  const handleSelectedRows: OnSelectedRows = useCallback(
    (newSelectedRows: RowData[]) => setSelectedRows(newSelectedRows),
    [setSelectedRows]
  )

  const handleDismissRows = useCallback(async () => {
    const selectedRowIds = selectedRows.reduce((memo, { id }) => {
      if (id) memo.push(parseInt(id, 10))
      return memo
    }, [])

    const byRowIds = { rowIds: selectedRowIds }
    if (selectedRowIds.length) {
      setValidatingTable(true)
      // if rows are selected then validate based on row ID's
      const mutationVariables: queueUpdateRecordStatusVariables = {
        validationState: EValidationState.dismissed,
        workbookId: batch.workbookId,
        schemaId: parseInt(batch.schemaId, 10),
        ...byRowIds
      }
      await queueUpdateRecordStatus.run(mutationVariables)
    }

    setSelectedRows([])
  }, [selectedRows, setSelectedRows])

  const handleOnDataLoad = useCallback(
    async ({ pageSize, page }: OnDataLoadProps) => {
      const skip = (page - 1) * pageSize < 0 ? 0 : (page - 1) * pageSize

      return fetchMoreRows({
        skip,
        limit: pageSize
      })
    },
    [fetchMoreRows]
  )

  const handleOnSort = useCallback(
    ({ columnIndex, contextMenu, order }: OnSortProps) => {
      //turntable rerenders every-time fetchWorkbookRows is called. This is a duplication of turntable logic required
      //until the table does not rerender when data is fetched.
      if (!contextMenu) {
        const { newSortOrder, newSelectedColumn } = determineSort({
          selectedColumn: columnIndex,
          currentSortOrder: sortOverride.sortOrder,
          previousColumn: sortOverride.sortColumn
        })
        setSortOverride({ sortOrder: newSortOrder, sortColumn: newSelectedColumn })
      } else {
        setSortOverride({ sortOrder: order, sortColumn: columnIndex })
      }
    },
    [sortOverride, labels, setSortOverride, refreshingDataset]
  )

  const handleCellsChange = useCallback(
    async ({ changes }: DataChangeEvent): Promise<DataChangeEvent> => {
      const schema = new Schema({ properties: schemaProperties })
      const edits = Transport.toPatch(changes, schema)
      const updatedRows = await updateValidationRows.run({
        batchId: batch.batchId,
        edits
      })
      const updatedChanges = updatedRows.rows
        .map((r, index) => {
          const change =
            changes.find((c) => parseInt(c.id, 10) === r._id) ??
            newRows.find((row: RowData) => parseInt(row.id, 10) === r._id) ??
            changes[index]

          if (!change) return null
          return DALTransport.fromDAL(schema, r, change.position).toTurntablePatch()
        })
        .filter((update) => update !== null)

      return { changes: updatedChanges }
    },
    [schemaProperties, batch, newRows]
  )
  /* ** */

  const DiscardInvalidRecordsModal = useConfirmModal({
    cancelColor: 'danger',
    cancelLabel: t('scenes.5_ReviewScene.cancelLabel'),
    confirmColor: 'primary',
    confirmLabel: t('scenes.5_ReviewScene.confirmLabel'),
    header: t(theme.textOverrides.discardModalHeader),
    contents: !loading && (
      <p>
        {t(theme.textOverrides.discardModalDescriptionBeginning)}
        <strong>
          {newCounts?.invalid === 1
            ? t(theme.textOverrides.discardModalDescription_one, {
                count: newCounts?.invalid
              })
            : t(theme.textOverrides.discardModalDescription_other, {
                count: newCounts?.invalid
              })}
        </strong>
        {t(theme.textOverrides.discardModalDescriptionEnd)}
      </p>
    ),
    onCancel: async () => {
      onBatchAction('evaluate')
      DiscardInvalidRecordsModal.close()
    },
    onConfirm: () => {
      setTableFilters({ filter: 'invalid' })
      setRefreshingDataset(true)
      setRefreshingDataset(false)
      DiscardInvalidRecordsModal.close()
    },
    theme
  })
  /* Elements */

  const filterWithCounts = useFilterWithCounts(
    newCounts,
    false,
    errors,
    schemaProperties,
    false,
    validating || isEmpty
  )

  const buttonText: JSX.Element | string = useMemo(() => {
    if (newCounts && !Object.keys(newCounts).length) {
      return <EllipsisLoader text={t('elements.loaders.loading')} />
    }

    if (saving) {
      return <EllipsisLoader text={t('elements.loaders.saving')} />
    }

    if (batch?.synced && !!newCounts) {
      return t(
        newCounts?.valid > 1
          ? theme.textOverrides.importRecords_other
          : theme.textOverrides.importRecords_one,
        { count: newCounts?.valid }
      )
    }

    return t(theme.textOverrides.saveAndFinalize)
  }, [newCounts, hasNoValidRows])
  /* ** */

  /* Constants for rendering logic */
  const selectedRowCount = selectedRows.length
  const selectedRowPositions = useMemo(() => {
    if (!selectedRowCount) return []
    if (selectedRowCount === 1) {
      return [selectedRows[0].position]
    }
    return [selectedRows[0].position, selectedRows[selectedRowCount - 1].position]
  }, [selectedRows])

  const showInnerLoading = isEmpty // Not good practice, but until useFetchWorkbookRows is updated, no row data is equivalent to loading
  const showError = !isEmpty && !isLoadingData && !schemaProperties
  return (
    <>
      {DiscardInvalidRecordsModal.render()}
      <Layout.FullContent>
        <Layout.FlexVertical>
          <Header scene={SCENE.REVIEW} />
          {!isEmpty && (
            <EmbedsErrorBanner
              batchId={batch.batchId}
              allInvalid={hasNoValidRows && !(batch.status === 'evaluate')}
            />
          )}
          {batch.status === 'evaluate' && (
            <RecordStatusBanner
              onDiscard={() => onBatchAction('submit')}
              recordSuccessCount={accepted}
              recordRejectedCount={review}
            />
          )}
          <ControlBar>
            {filterWithCounts}
            {selectedRowCount > 0 && (
              <DismissButton color='secondary' onClick={handleDismissRows}>
                {t('scenes.5_ReviewScene.discardSelected', { count: selectedRowCount })}
              </DismissButton>
            )}
          </ControlBar>
          {showError ? (
            <EmptyState>{t('scenes.5_ReviewScene.generalEmptyState')}</EmptyState>
          ) : showInnerLoading ? (
            loadingTimeoutError ? (
              <EmptyState>{t('scenes.5_ReviewScene.specificEmptyState')}</EmptyState>
            ) : (
              <Loader top={0} message={t('elements.loaders.loading') + '...'} />
            )
          ) : (
            <DataTable
              pageSize={PAGE_SIZE}
              tableHeaders={labels}
              tableRows={rows}
              totalCount={filtered ?? totalRows ?? rows?.length}
              onCellsChange={handleCellsChange}
              onSelectedRows={handleSelectedRows}
              selectedRows={selectedRowPositions}
              onDataLoad={handleOnDataLoad}
              onSort={handleOnSort}
              sortOverride={sortOverride}
              isLoading={isLoadingData}
              isLoadingFullPage={isLoadingFullPage}
              additionalRows={100}
              key={totalCount}
            />
          )}
        </Layout.FlexVertical>
      </Layout.FullContent>
      <FlexEndFooter boxShadow>
        <GoBackButton useMarginLeft={true} onClick={() => onGoBack(SCENE.MATCH)}>
          {t(theme.textOverrides.backToMatching)}
        </GoBackButton>
        <FooterButton
          disabled={saving || validating || isEmpty || (hasNoValidRows && batch.synced)}
          onClick={() => handleComplete()}
        >
          {buttonText}
        </FooterButton>
      </FlexEndFooter>
    </>
  )
}
